import { isNullish } from '@breezy/shared'
import { datadogRum } from '@datadog/browser-rum'
import { getConfig } from '../config'

export type RumEventName =
  | 'connectivity_status_change'
  | 'trpc_network_error'
  | 'trpc_http_error'
  | 'urql_network_error'
  | 'urql_graphql_error'
  | 'error_modal_shown'
  | 'network_toast_shown'
  | 'network_toast_cleared'
  | 'google_places_error'
  | 'page_loading_timeout'
  | 'global_search.open'
  | 'global_search.submit'
  | 'global_search.results_rendered'
  | 'global_search.result_click'
  | 'global_search.close'

type RumAttributes = Record<string, unknown>

const isRumReady = (): boolean => !isNullish(datadogRum.getInitConfiguration())

const RAW_QUERY_ATTRIBUTE_KEYS = new Set([
  'query',
  'querytext',
  'rawquery',
  'rawquerytext',
  'searchquery',
  'searchquerytext',
])

const normalizeAttributeKey = (key: string): string => key.replace(/[^a-z0-9]/gi, '').toLowerCase()

const isRawQueryAttributeKey = (key: string): boolean => RAW_QUERY_ATTRIBUTE_KEYS.has(normalizeAttributeKey(key))

const sanitizeRumAttributeValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(sanitizeRumAttributeValue)
  }

  if (!isNullish(value) && typeof value === 'object' && !(value instanceof Date) && !(value instanceof Error)) {
    return sanitizeRumAttributes(value as RumAttributes)
  }

  return value
}

export const sanitizeRumAttributes = (attrs: RumAttributes): RumAttributes =>
  Object.fromEntries(
    Object.entries(attrs).flatMap(([key, value]) =>
      isRawQueryAttributeKey(key) ? [] : [[key, sanitizeRumAttributeValue(value)]],
    ),
  )

const baseContext = (): RumAttributes => {
  const config = getConfig()

  return {
    target: config.breezyApplicationTargetName,
    env: config.env,
    platformVersion: config.platformVersion,
    appVersion: config.appVersion,
    onLine: navigator.onLine,
    visibilityState: document.visibilityState,
    timestamp: Date.now(),
  }
}

const normalizeError = (error: unknown) => {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
      cause: error.cause instanceof Error ? { name: error.cause.name, message: error.cause.message } : undefined,
    }
  }

  if (typeof error === 'string') {
    return { name: 'Error', message: error }
  }

  return { name: 'UnknownError', message: 'Unknown error type' }
}

export const rumEvent = (name: RumEventName, attrs?: RumAttributes): void => {
  if (!isRumReady()) return

  datadogRum.addAction(name, {
    ...baseContext(),
    ...sanitizeRumAttributes(attrs ?? {}),
  })
}

export const rumError = (name: RumEventName, error: unknown, attrs?: RumAttributes): void => {
  if (!isRumReady()) return

  datadogRum.addError(normalizeError(error), {
    event: name,
    ...baseContext(),
    ...sanitizeRumAttributes(attrs ?? {}),
  })
}
